import React from 'react';
import {
  Badge,
  Box,
  Flex,
  SimpleGrid,
  Text,
  chakra,
} from '@chakra-ui/react';

export const LIKERT_OPTIONS = [
  { value: 1, label: 'Totalmente en desacuerdo' },
  { value: 2, label: 'En desacuerdo' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'De acuerdo' },
  { value: 5, label: 'Totalmente de acuerdo' },
] as const;

type LikertAnswerOptionsProps = {
  questionId: number;
  questionText: string;
  value?: number;
  onChange: (value: number) => void;
  labels?: readonly string[];
  selectedLabel?: string;
};

const NativeRadio = chakra('input');

export const LikertAnswerOptions: React.FC<LikertAnswerOptionsProps> = ({
  questionId,
  questionText,
  value,
  onChange,
  labels,
  selectedLabel = 'Seleccionada',
}) => (
  <SimpleGrid
    role="radiogroup"
    aria-label={questionText}
    columns={{ base: 1, md: 2, xl: 5 }}
    spacing={3}
  >
    {LIKERT_OPTIONS.map((option, optionIndex) => {
      const isSelected = value === option.value;
      const label = labels?.[optionIndex] ?? option.label;
      const handleArrowNavigation = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const movement = {
          ArrowRight: 1,
          ArrowDown: 1,
          ArrowLeft: -1,
          ArrowUp: -1,
        }[event.key];

        if (!movement) {
          return;
        }

        event.preventDefault();
        const nextIndex = (optionIndex + movement + LIKERT_OPTIONS.length) % LIKERT_OPTIONS.length;
        const nextOption = LIKERT_OPTIONS[nextIndex];
        onChange(nextOption.value);

        const selector = `input[name="question-${questionId}"][value="${nextOption.value}"]`;
        window.requestAnimationFrame(() => {
          document.querySelector<HTMLInputElement>(selector)?.focus();
        });
      };

      return (
        <Box
          as="label"
          key={option.value}
          minH="56px"
          width="100%"
          p={3}
          display="flex"
          alignItems="flex-start"
          cursor="pointer"
          borderWidth="2px"
          borderRadius="md"
          borderColor={isSelected ? 'blue.700' : 'gray.400'}
          bg={isSelected ? 'blue.50' : 'white'}
          color="gray.900"
          boxShadow={isSelected ? '0 0 0 1px var(--chakra-colors-blue-700)' : 'none'}
          transition="border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease"
          _hover={{ borderColor: isSelected ? 'blue.800' : 'gray.600' }}
          _focusWithin={{
            borderColor: 'blue.800',
            boxShadow: '0 0 0 3px var(--chakra-colors-blue-200)',
          }}
        >
          <NativeRadio
            type="radio"
            name={`question-${questionId}`}
            value={option.value}
            checked={isSelected}
            onChange={() => onChange(option.value)}
            onKeyDown={handleArrowNavigation}
            width="22px"
            height="22px"
            minWidth="22px"
            mt="1px"
            cursor="pointer"
            sx={{ accentColor: 'var(--chakra-colors-blue-700)' }}
          />
          <Flex
            minW={0}
            width="100%"
            direction="column"
            align="flex-start"
            gap={1}
            pl={3}
          >
            <Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'medium'}>
              {label}
            </Text>
            {isSelected && (
              <Badge aria-hidden="true" colorScheme="blue" variant="solid" fontSize="xs">
                {selectedLabel}
              </Badge>
            )}
          </Flex>
        </Box>
      );
    })}
  </SimpleGrid>
);
